import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { BrandLockup } from "@/components/landing/LogoMark";
import { platformClient } from "./platformApi";

export default function PaymentReturn() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const cancelled = params.get("geannuleerd");
    const [state, setState] = useState(cancelled ? "cancelled" : "loading");
    const [invoiceNumber, setInvoiceNumber] = useState("");
    const attempts = useRef(0);

    useEffect(() => {
        if (cancelled || !sessionId) return;
        let timer;
        const poll = async () => {
            attempts.current += 1;
            try {
                const { data } = await platformClient.get(`/billing/pay/status/${sessionId}`);
                setInvoiceNumber(data.invoice_number || "");
                if (data.payment_status === "paid") {
                    setState("paid");
                    return;
                }
                if (["failed", "expired"].includes(data.status)) {
                    setState("failed");
                    return;
                }
            } catch (err) {
                if (err.response?.status === 401 || err.response?.status === 403) {
                    setState("unauthorized");
                    return;
                }
            }
            if (attempts.current < 15) {
                timer = setTimeout(poll, 2000);
            } else {
                setState("timeout");
            }
        };
        poll();
        return () => clearTimeout(timer);
    }, [sessionId, cancelled]);

    return (
        <div data-testid="payment-return-page" className="flex min-h-screen items-center justify-center bg-ink px-6 text-bone">
            <div className="noise-overlay" aria-hidden="true" />
            <div className="w-full max-w-md border border-white/10 bg-coal p-10 text-center">
                <BrandLockup emblemClass="h-12 w-12" textClass="text-xl" align="center" />

                {state === "loading" ? (
                    <div className="mt-10">
                        <Loader2 className="mx-auto h-8 w-8 animate-spin text-gold" />
                        <p className="mt-6 font-display text-2xl tracking-tight">Betaling verwerken…</p>
                        <p className="mt-3 text-sm text-ash">Een ogenblik — we bevestigen je betaling bij Stripe.</p>
                    </div>
                ) : state === "paid" ? (
                    <div className="mt-10">
                        <CheckCircle2 className="mx-auto h-10 w-10 text-gold" />
                        <p data-testid="payment-success" className="mt-6 font-display text-2xl tracking-tight">
                            Betaling ontvangen
                        </p>
                        <p className="mt-3 text-sm text-ash">
                            Bedankt! Factuur {invoiceNumber} is betaald en bijgewerkt in je overzicht.
                        </p>
                    </div>
                ) : state === "cancelled" ? (
                    <div className="mt-10">
                        <XCircle className="mx-auto h-10 w-10 text-ash" />
                        <p data-testid="payment-cancelled" className="mt-6 font-display text-2xl tracking-tight">
                            Betaling geannuleerd
                        </p>
                        <p className="mt-3 text-sm text-ash">
                            Er is niets afgeschreven. Je kunt de betaling opnieuw starten vanuit je overzicht.
                        </p>
                    </div>
                ) : state === "unauthorized" ? (
                    <div className="mt-10">
                        <p className="font-display text-2xl tracking-tight">Even opnieuw inloggen</p>
                        <p className="mt-3 text-sm text-ash">
                            Je sessie is verlopen. Log in om de betaalstatus te zien — een geslaagde betaling wordt altijd verwerkt.
                        </p>
                    </div>
                ) : (
                    <div className="mt-10">
                        <p data-testid="payment-pending" className="font-display text-2xl tracking-tight">
                            Betaling nog in behandeling
                        </p>
                        <p className="mt-3 text-sm text-ash">
                            De bevestiging laat even op zich wachten. Zodra Stripe de betaling afrondt, werkt je factuurstatus automatisch bij.
                        </p>
                    </div>
                )}

                <a
                    data-testid="payment-back-link"
                    href="/platform"
                    className="mt-10 inline-block border border-gold/50 px-6 py-3 text-xs uppercase tracking-[0.2em] text-gold transition-colors hover:bg-gold hover:text-black"
                >
                    Terug naar mijn omgeving
                </a>
            </div>
        </div>
    );
}
